import { useEffect, useState } from "react";
import api, { downloadFile, STATUS_STYLES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, Map, TrendingUp, FileDown, FileSpreadsheet, ClipboardCheck, Award } from "lucide-react";
import { toast } from "sonner";

function ExportButtons({ path, name, disabled, testid }) {
  const [busy, setBusy] = useState(false);
  const dl = async (fmt) => {
    setBusy(true);
    try {
      await downloadFile(`${path}/${fmt}`, `${name}.${fmt === "pdf" ? "pdf" : "xlsx"}`);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not generate report");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-3 mt-auto pt-5">
      <Button data-testid={`${testid}-pdf`} variant="outline" className="flex-1" disabled={disabled || busy} onClick={() => dl("pdf")}>
        <FileDown className="w-4 h-4 mr-2" />PDF
      </Button>
      <Button data-testid={`${testid}-excel`} variant="outline" className="flex-1" disabled={disabled || busy} onClick={() => dl("excel")}>
        <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
      </Button>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [regions, setRegions] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [region, setRegion] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [statusData, setStatusData] = useState(null);
  const [auditorRows, setAuditorRows] = useState([]);

  const canRegional = ["corporate_admin", "regional_head"].includes(user.role);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    if (canRegional) api.get("/reports/auditors").then((r) => setAuditorRows(r.data.rows)).catch(() => {});
  }, [canRegional]);

  useEffect(() => {
    if (!templateId) return;
    setStatusData(null);
    api.get(`/reports/template/${templateId}/status`).then((r) => setStatusData(r.data)).catch(() => toast.error("Could not load audit status"));
  }, [templateId]);

  useEffect(() => {
    api.get("/hotels").then((r) => {
      setHotels(r.data);
      if (user.role === "hotel_gm" && user.hotel_id) setHotelId(user.hotel_id);
    });
    api.get("/meta").then((r) => {
      const regs = (r.data.regions || []).filter(Boolean);
      setRegions(user.role === "regional_head" ? regs.filter((x) => x === user.region) : regs);
    });
  }, [user]);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Reports</h1>
        <p className="text-stone-500 mt-1 text-sm">Export summary reports in PDF or Excel.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 flex flex-col">
          <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
          </div>
          <h3 className="mt-4 font-bold text-stone-900">Hotel Summary</h3>
          <p className="text-xs text-stone-500 mt-1 mb-4">All audits, department performance and open actions for one hotel.</p>
          <Select value={hotelId} onValueChange={setHotelId} disabled={user.role === "hotel_gm"}>
            <SelectTrigger data-testid="report-hotel-select"><SelectValue placeholder="Choose hotel" /></SelectTrigger>
            <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
          </Select>
          <ExportButtons testid="report-hotel" path={`/reports/hotel/${hotelId}`} name="hotel-summary" disabled={!hotelId} />
        </div>

        {canRegional && (
          <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 flex flex-col">
            <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
              <Map className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 font-bold text-stone-900">Regional Summary</h3>
            <p className="text-xs text-stone-500 mt-1 mb-4">Hotel rankings, scores and action counts across a region.</p>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="report-region-select"><SelectValue placeholder="Choose region" /></SelectTrigger>
              <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <ExportButtons testid="report-region" path={`/reports/region/${encodeURIComponent(region)}`} name="regional-summary" disabled={!region} />
          </div>
        )}

        <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 flex flex-col">
          <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
          </div>
          <h3 className="mt-4 font-bold text-stone-900">Portfolio Trend Report</h3>
          <p className="text-xs text-stone-500 mt-1 mb-4">Monthly compliance trend, hotel rankings and department benchmarking.</p>
          <div className="text-xs text-stone-400 border border-dashed border-stone-200 rounded-lg px-3 py-2.5">Scoped to hotels you have access to.</div>
          <ExportButtons testid="report-trend" path="/reports/trend" name="trend-report" disabled={false} />
        </div>

        <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 flex flex-col">
          <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
          </div>
          <h3 className="mt-4 font-bold text-stone-900">Audit-based Report</h3>
          <p className="text-xs text-stone-500 mt-1 mb-4">Pick an audit — see which hotels have it pending, overdue or done, with scores.</p>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger data-testid="report-template-select"><SelectValue placeholder="Choose audit template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <ExportButtons testid="report-template" path={`/reports/template/${templateId}`} name="audit-status-report" disabled={!templateId} />
        </div>

        {canRegional && (
          <div className="bg-white border border-[#E7E5DF] rounded-xl p-6 flex flex-col">
            <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
              <Award className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 font-bold text-stone-900">Auditor Scorecard</h3>
            <p className="text-xs text-stone-500 mt-1 mb-4">Completed audits, average scores and on-time rates for every auditor.</p>
            <div className="text-xs text-stone-400 border border-dashed border-stone-200 rounded-lg px-3 py-2.5">Scoped to audits you have access to — table below.</div>
            <ExportButtons testid="report-auditors" path="/reports/auditors" name="auditor-scorecard" disabled={auditorRows.length === 0} />
          </div>
        )}
      </div>

      {canRegional && auditorRows.length > 0 && (
        <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto" data-testid="auditor-scorecard-table">
          <div className="px-6 py-4 border-b border-[#E7E5DF]">
            <h3 className="font-bold text-stone-900">Auditor Scorecard</h3>
          </div>
          <table className="w-full min-w-[720px]">
            <thead className="bg-stone-50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                <th className="py-3 px-4">Auditor</th>
                <th className="py-3 px-4 text-right">Assigned</th>
                <th className="py-3 px-4 text-right">Completed</th>
                <th className="py-3 px-4 text-right">Completion %</th>
                <th className="py-3 px-4 text-right">Avg Score</th>
                <th className="py-3 px-4 text-right">On-Time %</th>
                <th className="py-3 px-4 text-right">Open Actions</th>
              </tr>
            </thead>
            <tbody>
              {auditorRows.map((r) => (
                <tr key={r.auditor} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60" data-testid={`auditor-row-${r.auditor}`}>
                  <td className="py-3.5 px-4 text-sm font-medium text-stone-800">{r.auditor}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.assigned}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.completed}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.completion_rate}%</td>
                  <td className={`py-3.5 px-4 text-right font-mono text-sm font-semibold ${r.avg_score == null ? "text-stone-400" : r.avg_score >= 85 ? "text-emerald-600" : r.avg_score >= 70 ? "text-amber-600" : "text-rose-600"}`}>{r.avg_score != null ? `${r.avg_score}%` : "—"}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.on_time_rate != null ? `${r.on_time_rate}%` : "—"}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.open_actions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {statusData && (
        <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto" data-testid="template-status-table">
          <div className="px-6 py-4 border-b border-[#E7E5DF]">
            <h3 className="font-bold text-stone-900">{statusData.template_name} — hotel-wise status</h3>
          </div>
          <table className="w-full min-w-[720px]">
            <thead className="bg-stone-50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                <th className="py-3 px-4">Hotel</th>
                <th className="py-3 px-4">Region</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Next Due</th>
                <th className="py-3 px-4 text-right">Latest Score</th>
                <th className="py-3 px-4">Last Audit</th>
                <th className="py-3 px-4 text-right">Done</th>
              </tr>
            </thead>
            <tbody>
              {statusData.rows.map((r) => (
                <tr key={r.hotel} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                  <td className="py-3.5 px-4 text-sm font-medium text-stone-800">{r.hotel}</td>
                  <td className="py-3.5 px-4 text-sm text-stone-600">{r.region}</td>
                  <td className="py-3.5 px-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      r.status === "overdue" ? "bg-rose-50 text-rose-700 border-rose-200"
                      : r.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                      : r.status === "completed" ? STATUS_STYLES.completed
                      : "bg-stone-100 text-stone-500 border-stone-200"}`}>
                      {r.status.replace("_", " ")}{r.pending_count > 1 ? ` ×${r.pending_count}` : ""}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-sm font-mono text-stone-600">{r.next_due || "—"}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm font-semibold">{r.latest_score != null ? `${r.latest_score}%` : "—"}</td>
                  <td className="py-3.5 px-4 text-sm font-mono text-stone-600">{r.last_audit_date || "—"}</td>
                  <td className="py-3.5 px-4 text-right font-mono text-sm">{r.audits_done}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
